import {
  BarChart3Icon,
  BookOpenIcon,
  CalendarDaysIcon,
  ContactIcon,
  CreditCardIcon,
  FileTextIcon,
  FolderIcon,
  GraduationCapIcon,
  KeyRoundIcon,
  LayersIcon,
  LayoutGridIcon,
  LayoutTemplateIcon,
  MessageSquareIcon,
  PhoneCallIcon,
  ReceiptIcon,
  RefreshCwIcon,
  RocketIcon,
  ShareIcon,
  SmartphoneIcon,
  SparklesIcon,
  StarIcon,
  UserPlusIcon,
  UsersIcon,
  WorkflowIcon,
  ZapIcon,
} from 'lucide-react'
import type { Section } from '../types/onboarding'

export const sections: Section[] = [
  {
    id: 'logging-in',
    number: 1,
    navLabel: 'Logging in',
    title: 'Logging in',
    purpose: 'Get into your account for the first time and make it yours.',
    icon: KeyRoundIcon,
    kind: 'steps',
    mediaSlots: ['The login screen'],
    steps: [
      'Open your welcome email and click the login link, or go straight there: [Login Here](https://crm.viridiaanalytics.com/).',
      'On the "Sign into your account" screen, enter your email address and password.',
      'Prefer Google? Click "Sign in with Google" and use your business Google account instead.',
      'Bookmark the login page on your phone and computer so it\'s always one tap away.',
    ],
    notes: [
      'First time, or forgot your password? Click "Forgot password?" on the login screen to set it.',
      'Can\'t find the welcome email? Check your spam folder, then reach out and we\'ll resend it.',
    ],
  },
  {
    id: 'creating-users',
    number: 2,
    navLabel: 'Add your team',
    title: 'Creating users (add your team)',
    purpose: 'Give your office staff and techs their own logins.',
    icon: UsersIcon,
    kind: 'steps',
    mediaSlots: ['Settings → My Staff → + Add User'],
    steps: [
      'Go to Settings → My Staff.',
      'Click the "+ Add User" button.',
      'Fill in their first name, last name, email and phone number.',
      'Pick their role — "Account User" is right for most staff; choose Admin only for people who should manage settings and billing.',
      'Click Save — they\'ll get an invite email with their own login details.',
    ],
    notes: [
      'Not sure who needs Admin? Start everyone as an Account User — you can change it any time.',
    ],
  },
  {
    id: 'crm-tour',
    number: 3,
    navLabel: 'Tour of your CRM',
    title: 'A tour of your CRM',
    purpose: 'A quick lap around the left-hand menu so nothing feels unfamiliar.',
    icon: BarChart3Icon,
    kind: 'tour',
    mediaSlots: ['The left-hand menu'],
    steps: [
      'Use the left-hand menu to move between areas — it stays with you everywhere.',
      'Click an area to open it; the menu highlights where you are.',
      'Have a click around — you can\'t break anything.',
    ],
    areas: [
      {
        name: 'Ask AI',
        icon: SparklesIcon,
        description: 'Ask questions about your business in plain English.',
      },
      {
        name: 'Launchpad',
        icon: RocketIcon,
        description: 'Your setup checklist and quick-start shortcuts.',
      },
      {
        name: 'Dashboard',
        icon: BarChart3Icon,
        description: 'Your at-a-glance numbers — new leads, calls and revenue.',
      },
      {
        name: 'Conversations',
        icon: MessageSquareIcon,
        description:
          'Every call, text and email in one inbox. This is where "never miss a lead" happens.',
        highlight: true,
      },
      {
        name: 'Calendars',
        icon: CalendarDaysIcon,
        description: 'Your bookings and the hours you\'re available for work.',
      },
      {
        name: 'Contacts',
        icon: ContactIcon,
        description: 'Your full list of customers and leads, always searchable.',
      },
      {
        name: 'Opportunities',
        icon: LayersIcon,
        description:
          'The stages a job moves through — drag a card along as it progresses. The default pipeline works great; custom ones are optional.',
      },
      {
        name: 'Payments',
        icon: CreditCardIcon,
        description: 'Invoices, estimates, and getting paid.',
      },
    ],
  },
  {
    id: 'creating-a-contact',
    number: 4,
    navLabel: 'Create a contact',
    title: 'Creating a contact',
    purpose: 'Add a customer by hand — handy for jobs that come in word-of-mouth.',
    icon: UserPlusIcon,
    kind: 'steps',
    mediaSlots: ['The + Add Contact button'],
    steps: [
      'Go to Contacts.',
      'Click "+ Add Contact" in the top right.',
      'Enter their name, phone number and email address.',
      'Click Save — they\'re now in your system and searchable.',
    ],
    notes: [
      'Already have a customer list? Use "Import" to bring them all in at once from a spreadsheet — send it our way and we\'ll help you get it in.',
    ],
  },
  {
    id: 'calendar-sync',
    number: 5,
    navLabel: 'Calendar sync',
    title: 'Calendar sync',
    purpose: 'Keep your bookings and your personal calendar in step.',
    icon: RefreshCwIcon,
    kind: 'steps',
    mediaSlots: ['Connect your calendar'],
    steps: [
      'Go to Calendars, then open its settings.',
      'Find "Easily connect your third-party calendar(s)".',
      'Click "Connect" next to Google, Outlook, iCloud or Calendly — whichever you use.',
      'Sign in and allow access so new bookings land on your calendar and your personal events block out your availability.',
      'Optional: set your available hours so you only get booked when you\'re actually working.',
    ],
    notes: [
      'Connecting your email lives nearby too — once it\'s on, replies land in Conversations.',
    ],
  },
  {
    id: 'social-accounts',
    number: 6,
    navLabel: 'Social accounts',
    title: 'Connect your social accounts',
    purpose: 'Post to all your channels from one place — and catch messages there too.',
    icon: ShareIcon,
    kind: 'steps',
    mediaSlots: ['Social Planner — connect accounts'],
    steps: [
      'Go to Marketing → Social Planner.',
      'Look for "Connect your social accounts".',
      'Click "+ Connect" next to the platform you want: Facebook, Instagram, LinkedIn, TikTok or YouTube (and more).',
      'Sign in to that account and approve the connection.',
      'Repeat for each channel you actually post to.',
    ],
    notes: ['Only connect what you use — you can always add more later.'],
  },
  {
    id: 'invoicing',
    number: 7,
    navLabel: 'Invoicing',
    title: 'Invoicing',
    purpose:
      'Send your invoices through Viridia — every closed job ties back to the lead that made it, so you (and we) can see exactly what your marketing is producing.',
    icon: ReceiptIcon,
    kind: 'steps',
    mediaSlots: ['Payments → Invoices → + New'],
    steps: [
      'Go to Payments → Invoices.',
      'Click "+ New".',
      'Add the customer, then add your line items — the services and prices for this job.',
      'Click Send — it goes straight to their inbox.',
    ],
    notes: [
      'Invoicing here is what closes the loop: the revenue from each job is linked to the lead that created it.',
    ],
  },
  {
    id: 'ai-knowledge-base',
    number: 8,
    navLabel: 'Voice AI knowledge base',
    title: 'Voice AI — knowledge base',
    purpose: 'What your AI receptionist knows about your business, so it answers like you would.',
    icon: BookOpenIcon,
    kind: 'white-glove',
    mediaSlots: ['AI Agents → Voice AI'],
    togetherPoints: [
      'Your opening hours and after-hours handling',
      'The services you offer — and the ones you don\'t',
      'Rough pricing or call-out fees you\'re happy to share',
      'Your service area',
      'How you\'d like callers greeted',
    ],
    notes: [
      'It lives under "AI Agents" → the "Voice AI" and "Knowledge Base" tabs. Nothing to set up on your own — we\'ll walk through it with you on a call and write it all up for you. Voice AI is set up on the desktop app.',
    ],
  },
  {
    id: 'ai-agent-number',
    number: 9,
    navLabel: 'Voice AI number',
    title: 'Voice AI — agent & phone number',
    purpose: 'The dedicated number your AI answers on, 24/7.',
    icon: PhoneCallIcon,
    kind: 'white-glove',
    mediaSlots: ['AI Agents → agent & phone number'],
    togetherPoints: [
      'We help you choose a dedicated phone number',
      'We connect your Voice AI agent to that number',
      'We decide together how calls are forwarded from your existing line',
      'We test it side by side before it goes live',
    ],
    notes: [
      'Once it\'s live, every call is answered — day, night and weekends. Voice AI is set up on the desktop app.',
    ],
  },
  {
    id: 'explore-more',
    number: 10,
    navLabel: 'Explore everything else',
    title: 'Explore everything else',
    purpose: 'A few more tools waiting for you whenever you\'re ready.',
    icon: SparklesIcon,
    kind: 'explore',
    mediaSlots: ['Feature overview'],
    items: [
      {
        name: 'Automation',
        icon: WorkflowIcon,
        description: 'Hands-off follow-up so no lead goes cold.',
      },
      {
        name: 'Sites',
        icon: LayoutTemplateIcon,
        description: 'Websites and funnels that turn clicks into calls.',
      },
      {
        name: 'Memberships',
        icon: GraduationCapIcon,
        description: 'Courses and communities for your customers or crew.',
      },
      {
        name: 'Reputation',
        icon: StarIcon,
        description: 'Collect reviews and lift your Google rating.',
      },
      {
        name: 'Reporting',
        icon: BarChart3Icon,
        description: 'Your numbers — calls, leads and revenue.',
      },
      {
        name: 'Media Storage',
        icon: FolderIcon,
        description: 'One home for your photos, logos and files.',
      },
      {
        name: 'App Marketplace',
        icon: LayoutGridIcon,
        description: 'Add-ons that plug into your account.',
      },
      {
        name: 'Mobile App',
        icon: SmartphoneIcon,
        description: 'Your CRM on your phone, between jobs.',
      },
      {
        name: 'Ask AI',
        icon: SparklesIcon,
        description: 'Answers about your business, in plain English.',
      },
      {
        name: 'Launchpad',
        icon: RocketIcon,
        description: 'Quick-start shortcuts to finish setting up.',
      },
    ],
  },
]

export const extras = {
  quickWins: [
    { icon: ZapIcon, label: 'Most owners finish steps 1–7 in about 25 minutes.' },
    { icon: FileTextIcon, label: 'No need to do it in one sitting — your progress is saved here.' },
  ],
}
